import { Box, Flex, Heading, SimpleGrid, Text, Icon } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { LuRefreshCw, LuSettings2, LuSparkles } from "react-icons/lu";
import { IconType } from "react-icons";
import { useLandingTheme, LandingTheme } from "../Index";

const MotionBox = motion.create(Box);

const Step = ({
  icon,
  number,
  title,
  description,
  delay,
  theme,
  isDark,
}: {
  icon: IconType;
  number: string;
  title: string;
  description: string;
  delay: number;
  theme: LandingTheme;
  isDark: boolean;
}) => (
  <MotionBox
    position="relative"
    zIndex={10}
    display="flex"
    flexDirection="column"
    alignItems="center"
    textAlign="center"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    role="group"
  >
    {/* Icon Box */}
    <Flex
      w={24}
      h={24}
      borderRadius="2xl"
      bg={isDark ? theme.surfaceMuted : theme.surface}
      border="1px solid"
      borderColor={theme.border}
      align="center"
      justify="center"
      mb={6}
      boxShadow={isDark ? "xl" : "lg"}
      transition="all 0.3s"
      _groupHover={{
        transform: "scale(1.1)",
        borderColor: `${theme.primary}60`,
        boxShadow: `0 10px 40px ${theme.primary}${isDark ? "33" : "20"}`,
      }}
    >
      <Icon as={icon} boxSize={10} color={theme.primary} />
    </Flex>

    {/* Step Title */}
    <Heading as="h3" fontSize="xl" fontWeight="bold" color={theme.textPrimary} mb={3}>
      {number}. {title}
    </Heading>

    {/* Description */}
    <Text fontSize="sm" color={theme.textSecondary} maxW="xs" mx="auto">
      {description}
    </Text>
  </MotionBox>
);

export const HowItWorks = () => {
  const { theme, isDark } = useLandingTheme();

  return (
    <Box as="section" py={24} px={4} w="full" position="relative" id="how-it-works" transition="background-color 0.3s">
      <Flex maxW="6xl" mx="auto" direction="column" align="center">
        {/* Section Header */}
        <Box mb={16} textAlign="center" maxW="2xl">
          <Heading as="h2" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" color={theme.textPrimary} mb={4}>
            How It Works
          </Heading>
          <Text color={theme.textSecondary}>Seamless integration into your existing workflow.</Text>
        </Box>

        {/* Steps Grid */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={8} position="relative" w="full">
          {/* Connector Line (Desktop) */}
          <Box
            display={{ base: "none", md: "block" }}
            position="absolute"
            top={12}
            left="16%"
            right="16%"
            h="2px"
            background={`linear-gradient(to right, transparent, ${theme.primary}${isDark ? "80" : "40"}, transparent)`}
            zIndex={0}
          />

          <Step
            icon={LuRefreshCw}
            number="1"
            title="Connect Calendar"
            description="One-click integration with Google Calendar securely. We respect your privacy first."
            delay={0}
            theme={theme}
            isDark={isDark}
          />
          <Step
            icon={LuSettings2}
            number="2"
            title="Set Preferences"
            description="Tell us your peak hours and preferred session lengths. Muro learns what works for you."
            delay={0.15}
            theme={theme}
            isDark={isDark}
          />
          <Step
            icon={LuSparkles}
            number="3"
            title="Auto-Focus"
            description="Muro automatically defends your time for deep work, blocking distractions before they happen."
            delay={0.3}
            theme={theme}
            isDark={isDark}
          />
        </SimpleGrid>
      </Flex>
    </Box>
  );
};
